import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "@/lib/useApi";
import { api, apiErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader } from "@/components/common";
import {
  Newspaper, Send, Trophy, Bell, PartyPopper, MessageSquare, Trash2, Image as ImageIcon,
  X, Users, Globe, ShieldAlert, Loader2, MessageCircle, Video, ScrollText, ClipboardList,
} from "lucide-react";
import { fDateTime } from "@/lib/format";
import { toast } from "sonner";

const viewedSet = new Set();

const CAT = {
  rekord: { tk: "cat_rekord", icon: Trophy, cls: "bg-amber-100 text-amber-800" },
  nyhet: { tk: "cat_nyhet", icon: Bell, cls: "bg-sky-100 text-sky-800" },
  halsning: { tk: "cat_halsning", icon: PartyPopper, cls: "bg-pink-100 text-pink-800" },
  policy: { tk: "cat_policy", icon: ScrollText, cls: "bg-indigo-100 text-indigo-800" },
  rutin: { tk: "cat_rutin", icon: ClipboardList, cls: "bg-teal-100 text-teal-800" },
  ovrigt: { tk: "cat_ovrigt", icon: MessageSquare, cls: "bg-slate-100 text-slate-700" },
};
const EMOJIS = ["👍", "🎉", "❤️", "💪", "👏"];
const AUD = [
  { v: "all", tk: "aud_all" }, { v: "business_area", tk: "aud_area" }, { v: "department", tk: "aud_dept" },
  { v: "team", tk: "aud_team" }, { v: "shift", tk: "aud_shift" },
];

function Composer({ refs, onPosted }) {
  const { t } = useLang();
  const [form, setForm] = useState({ title: "", body: "", category: "nyhet", audienceType: "all", audienceId: "", publish_target: "intern" });
  const [image, setImage] = useState(null); // {path}
  const [video, setVideo] = useState(null); // {path}
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gdpr, setGdpr] = useState(null);
  const fileRef = useRef(null);
  const videoRef = useRef(null);

  const opts = form.audienceType === "business_area" ? refs.businessAreas : form.audienceType === "department" ? refs.departments : form.audienceType === "team" ? refs.teams : form.audienceType === "shift" ? refs.shifts : [];

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage({ path: r.data.path });
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onVideoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVideo({ path: r.data.path });
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); if (videoRef.current) videoRef.current.value = ""; }
  };

  const buildAudience = () => form.audienceType === "all" ? { type: "all" } : { type: form.audienceType, ids: form.audienceId ? [form.audienceId] : [] };

  const publish = async (gdprFlags = []) => {
    setBusy(true);
    try {
      await api.post("/feed", {
        title: form.title, body: form.body, category: form.category, publish_target: form.publish_target,
        image_path: image?.path || null, video_path: video?.path || null, audience: buildAudience(), gdpr_flags: gdprFlags,
      });
      setForm({ title: "", body: "", category: "nyhet", audienceType: "all", audienceId: "", publish_target: "intern" });
      setImage(null); setVideo(null); setGdpr(null);
      onPosted();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!form.body.trim()) return toast.error(t("feed_write_something"));
    setBusy(true);
    try {
      const r = await api.post("/feed/gdpr-check", { text: `${form.title} ${form.body}` });
      if (r.data.flagged) { setGdpr(r.data); setBusy(false); return; }
      await publish([]);
    } catch (e) { setBusy(false); await publish([]); }
  };

  return (
    <Card className="mb-6 p-4" data-testid="feed-composer">
      <div className="grid gap-3 sm:grid-cols-3">
        <Input placeholder={t("feed_title_ph")} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="feed-title" className="sm:col-span-2" />
        <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
          <SelectTrigger data-testid="feed-cat"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(CAT).map(([k, v]) => <SelectItem key={k} value={k}>{t(v.tk)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Textarea rows={2} className="mt-3" placeholder={t("feed_body_ph")} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} data-testid="feed-body" />

      {image && (
        <div className="relative mt-3 w-fit">
          <img src={`${API}/files/${image.path}`} alt="" className="max-h-56 rounded-lg border object-cover" />
          <button onClick={() => setImage(null)} className="absolute -right-2 -top-2 rounded-full bg-background p-1 shadow" data-testid="feed-img-remove"><X className="h-4 w-4" /></button>
        </div>
      )}

      {video && (
        <div className="relative mt-3 w-fit">
          <video src={`${API}/files/${video.path}`} controls className="max-h-56 rounded-lg border" />
          <button onClick={() => setVideo(null)} className="absolute -right-2 -top-2 rounded-full bg-background p-1 shadow" data-testid="feed-video-remove"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-3 py-2 text-sm hover:bg-secondary" data-testid="feed-img-btn">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} {t("feed_img")}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="feed-img-input" />
        </label>
        <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border px-3 py-2 text-sm hover:bg-secondary" data-testid="feed-video-btn">
          <Video className="h-4 w-4" /> {t("feed_video")}
          <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={onVideoFile} data-testid="feed-video-input" />
        </label>
        <Select value={form.audienceType} onValueChange={(v) => setForm((f) => ({ ...f, audienceType: v, audienceId: "" }))}>
          <SelectTrigger className="w-36" data-testid="feed-aud-type"><SelectValue /></SelectTrigger>
          <SelectContent>{AUD.map((o) => <SelectItem key={o.v} value={o.v}>{t(o.tk)}</SelectItem>)}</SelectContent>
        </Select>
        {form.audienceType !== "all" &&
          <Select value={form.audienceId} onValueChange={(v) => setForm((f) => ({ ...f, audienceId: v }))}>
            <SelectTrigger className="w-40" data-testid="feed-aud-target"><SelectValue placeholder={t("choose")} /></SelectTrigger>
            <SelectContent>{(opts || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>}
        <Select value={form.publish_target} onValueChange={(v) => setForm((f) => ({ ...f, publish_target: v }))}>
          <SelectTrigger className="w-40" data-testid="feed-publish-target"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="intern">Internt</SelectItem>
            <SelectItem value="extern">Externt (sociala medier)</SelectItem>
            <SelectItem value="bada">Internt + externt</SelectItem>
          </SelectContent>
        </Select>
        <Button className="ml-auto" onClick={submit} disabled={busy || uploading} data-testid="feed-post"><Send className="mr-1 h-4 w-4" />{t("publish")}</Button>
      </div>

      <Dialog open={!!gdpr} onOpenChange={(o) => !o && setGdpr(null)}>
        <DialogContent data-testid="gdpr-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" />{t("gdpr_title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("gdpr_intro")}</p>
          <ul className="space-y-1 text-sm">{(gdpr?.reasons || []).map((r, i) => <li key={i} className="flex gap-2"><span className="text-amber-600">•</span>{r}</li>)}</ul>
          <p className="text-xs text-muted-foreground">{t("gdpr_hint")}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGdpr(null)} data-testid="gdpr-edit">{t("gdpr_edit")}</Button>
            <Button onClick={() => publish(gdpr?.reasons || [])} data-testid="gdpr-publish-anyway">{t("gdpr_publish_anyway")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Comments({ postId }) {
  const { t } = useLang();
  const { user, effectiveRole } = useAuth();
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const canMod = ["owner", "hr_admin"].includes(effectiveRole);
  const load = useCallback(async () => {
    try { const r = await api.get(`/feed/${postId}/comments`); setItems(r.data); } catch (e) { toast.error(apiErr(e)); }
  }, [postId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!text.trim()) return;
    try { await api.post(`/feed/${postId}/comments`, { body: text }); setText(""); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (cid) => { try { await api.delete(`/feed/comments/${cid}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="mt-3 border-t pt-3" data-testid={`comments-${postId}`}>
      <div className="space-y-2">
        {(items || []).map((c) => (
          <div key={c.id} className="flex items-start gap-2 text-sm">
            <div className="rounded-lg bg-muted px-3 py-1.5">
              <span className="font-medium">{c.author_name}</span> <span className="text-muted-foreground">{c.body}</span>
            </div>
            {(c.author === user?.email || canMod) &&
              <button onClick={() => del(c.id)} data-testid={`del-comment-${c.id}`}><Trash2 className="mt-1 h-3.5 w-3.5 text-red-400" /></button>}
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={t("comment_ph")} data-testid={`comment-input-${postId}`} />
        <Button size="icon" onClick={add} data-testid={`comment-send-${postId}`}><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

function audienceLabel(aud, refs, t) {
  if (!aud || aud.type === "all") return null;
  const map = aud.type === "business_area" ? refs.businessAreas : aud.type === "department" ? refs.departments : aud.type === "team" ? refs.teams : refs.shifts;
  const name = (map || []).find((x) => x.id === (aud.ids || [])[0])?.name;
  return name ? t("audience_only", { name }) : t("targeted");
}

function Post({ p, refs, canModerate, onChanged }) {
  const { t } = useLang();
  const { user, effectiveRole } = useAuth();
  const [open, setOpen] = useState(false);
  const cardRef = useRef(null);
  const c = CAT[p.category] || CAT.ovrigt;
  const Icon = c.icon;
  const audLbl = audienceLabel(p.audience, refs, t);
  const canDelete = p.author === user?.email || canModerate || ["owner", "hr_admin"].includes(effectiveRole);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || viewedSet.has(p.id)) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !viewedSet.has(p.id)) {
        viewedSet.add(p.id);
        api.post(`/feed/${p.id}/view`).catch(() => {});
        obs.disconnect();
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [p.id]);

  const react = async (em) => { try { await api.post(`/feed/${p.id}/react`, { emoji: em }); onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  const remove = async () => { try { await api.delete(`/feed/${p.id}`); onChanged(); toast.success(t("deleted")); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <Card ref={cardRef} className="p-4" data-testid={`feed-${p.id}`}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${c.cls}`}><Icon className="h-3 w-3" />{t(c.tk)}</span>
        {p.publish_target && p.publish_target !== "intern" && (
          <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-800" data-testid={`feed-target-${p.id}`}>
            {p.publish_target === "bada" ? "Internt + externt" : "Externt"}
          </span>
        )}
        <span className="text-sm font-medium">{p.author_name}</span>
        {audLbl ? <Badge variant="outline" className="gap-1 text-[10px]"><Users className="h-3 w-3" />{audLbl}</Badge>
          : <Badge variant="outline" className="gap-1 text-[10px]"><Globe className="h-3 w-3" />{t("aud_all")}</Badge>}
        <span className="ml-auto text-xs text-muted-foreground">{fDateTime(p.created_at)}</span>
        {canDelete && <button onClick={remove} data-testid={`feed-del-${p.id}`}><Trash2 className="h-4 w-4 text-red-500" /></button>}
      </div>
      {p.title && <div className="font-head text-base font-semibold">{p.title}</div>}
      <p className="whitespace-pre-wrap text-sm text-muted-foreground">{p.body}</p>
      {p.image_path && <img src={`${API}/files/${p.image_path}`} alt="" className="mt-3 max-h-96 w-full rounded-lg border object-cover" data-testid={`feed-img-${p.id}`} />}
      {p.video_path && <video src={`${API}/files/${p.video_path}`} controls className="mt-3 max-h-96 w-full rounded-lg border bg-black" data-testid={`feed-video-${p.id}`} />}
      {canModerate && (p.gdpr_flags || []).length > 0 &&
        <div className="mt-2 flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-700"><ShieldAlert className="h-3.5 w-3.5" />{t("gdpr_flagged", { n: p.gdpr_flags.length })}</div>}

      <div className="mt-3 flex flex-wrap items-center gap-1">
        {EMOJIS.map((em) => {
          const list = (p.reactions || {})[em] || [];
          const mine = list.includes(user?.id);
          return (
            <button key={em} onClick={() => react(em)} data-testid={`react-${p.id}-${em}`}
              className={`rounded-full border px-2 py-0.5 text-sm transition-colors ${mine ? "border-accent bg-accent/10" : "hover:bg-muted"}`}>
              {em}{list.length > 0 && <span className="ml-1 text-xs">{list.length}</span>}
            </button>
          );
        })}
        <button onClick={() => setOpen((o) => !o)} className="ml-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-sm hover:bg-muted" data-testid={`toggle-comments-${p.id}`}>
          <MessageCircle className="h-4 w-4" />{p.comment_count || 0}
        </button>
      </div>
      {open && <Comments postId={p.id} />}
    </Card>
  );
}

export default function Feed() {
  const { t } = useLang();
  const { data: teams } = useApi("/teams");
  const { data: departments } = useApi("/departments");
  const { data: shifts } = useApi("/shifts");
  const { data: businessAreas } = useApi("/business_areas");
  const refs = { teams, departments, shifts, businessAreas };

  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [canModerate, setCanModerate] = useState(false);
  const [params, setParams] = useSearchParams();
  const focusId = params.get("post");
  const [focusPost, setFocusPost] = useState(null);
  const sentinel = useRef(null);

  useEffect(() => {
    if (!focusId) { setFocusPost(null); return; }
    api.get(`/feed/${focusId}`).then((r) => setFocusPost(r.data)).catch(() => setFocusPost(null));
  }, [focusId]);

  const load = useCallback(async (before) => {
    if (loading) return;
    setLoading(true);
    try {
      const r = await api.get("/feed", { params: { before: before || undefined, limit: 15 } });
      setCanModerate(r.data.can_moderate);
      setPosts((prev) => before ? [...prev, ...r.data.posts] : r.data.posts);
      setCursor(r.data.next_before);
      setHasMore(!!r.data.next_before);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, [loading]);

  const reload = useCallback(() => { setCursor(null); setHasMore(true); load(null); }, [load]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(null); }, []);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading && cursor) load(cursor);
    }, { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [cursor, hasMore, loading, load]);

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title={t("feed_title")} icon={Newspaper} subtitle={t("feed_sub")} />
      <Composer refs={refs} onPosted={reload} />

      {focusPost && (
        <div className="mb-4" data-testid="feed-focus">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-semibold text-accent">{t("feed_focus_label")}</span>
            <button onClick={() => setParams({})} className="text-xs text-muted-foreground underline" data-testid="feed-focus-clear">{t("feed_show_all")}</button>
          </div>
          <div className="rounded-xl ring-2 ring-accent">
            <Post p={focusPost} refs={refs} canModerate={canModerate}
              onChanged={() => { reload(); api.get(`/feed/${focusId}`).then((r) => setFocusPost(r.data)).catch(() => setFocusPost(null)); }} />
          </div>
        </div>
      )}

      {posts.length === 0 && !loading ?
        <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="feed-empty">
          <Newspaper className="mx-auto mb-2 h-8 w-8 text-accent/40" />{t("feed_empty")}
        </Card> :
        <div className="space-y-3">
          {posts.map((p) => <Post key={p.id} p={p} refs={refs} canModerate={canModerate} onChanged={reload} />)}
        </div>}

      <div ref={sentinel} className="py-6 text-center">
        {loading && <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />}
        {!hasMore && posts.length > 0 && <span className="text-xs text-muted-foreground">{t("feed_no_more")}</span>}
      </div>
    </div>
  );
}
